// packages
import dayjs from 'dayjs';

// components
import { Icon } from '@sdb/planning-ui';

// utils
import { themeExtension } from 'config/theme.extension';

// styles
import { IconContainer, IconLabel } from './mrp.styles';

// types
import type {
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated/index';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type {
  CalendarDisplayData,
  CalendarSourceData
} from './stores/calendarStore/calendarStore.types';

// const
import theme from 'config/theme';

export const getTimeSlotEvent = ({
  x,
  y
}: {
  x: number;
  y: number;
}): Date | null => {
  const elements = document.elementsFromPoint(x, y);
  const slot = elements.find((element) =>
    element.classList.contains('rbc-time-slot')
  ) as HTMLDivElement | undefined;
  if (!slot || !slot?.dataset?.timeStart) return null;

  return dayjs(slot.dataset.timeStart).toDate();
};

export const formatTime = (date: Date) => dayjs(date).format('HH:mm');
export const isWholeHour = (date: Date) => dayjs(date).minute() % 60 === 0;
export const getDurationInSeconds = (start: Date, end: Date) =>
  dayjs(end).diff(dayjs(start), 'seconds');

export const getCountPerEntityType = (
  planningEntityTypes: PlanningEntityType[]
) => {
  return planningEntityTypes.reduce(
    (acc, type) => {
      if (!acc[type]) acc[type] = 0;
      acc[type] = acc[type] + 1;
      return acc;
    },
    {} as Record<PlanningEntityType, number>
  );
};

export const getEntityCounters = (
  planningEntityTypes: PlanningEntityType[],
  iconSize?: 'MEDIUM' | 'LARGE'
) => {
  const planningCountMap = getCountPerEntityType(planningEntityTypes);

  return (
    <>
      {Object.entries(planningCountMap).map(
        ({ '0': planningEntityType, '1': count }) =>
          getEntityIcon(
            planningEntityType as PlanningEntityType,
            count.toString(),
            iconSize
          )
      )}
    </>
  );
};

export const getEntityIcon = (
  planningEntityType: PlanningEntityType | 'MIX',
  label?: string,
  iconSize?: 'SMALL' | 'MEDIUM' | 'LARGE',
  colorAsBackground?: boolean
) => {
  const iconType = getIconType(planningEntityType);
  return (
    <IconContainer
      key={planningEntityType}
      iconColor={getIconColor(planningEntityType)}
      colorAsBackground={colorAsBackground}
      iconSize={iconSize ?? 'SMALL'}
    >
      {iconType && <Icon type={iconType} />}
      <IconLabel variant="body1">{label}</IconLabel>
    </IconContainer>
  );
};

export const getIconType = (
  planningEntityType: PlanningEntityType | 'MIX'
): IconType => {
  switch (planningEntityType) {
    case 'EMPLOYEE':
      return 'user-doctor';
    case 'CLIENT':
      return 'user';
    case 'RESOURCE':
      return 'hammer-brush';
    case 'WORKPLACE':
      return 'house-chimney';
    case 'MIX':
      return 'calendar-star';
  }

  return '' as IconType;
};

export const getIconColor = (planningEntityType: string) => {
  const { client, employee, resource, workplace } =
    themeExtension.custom!.constants.MRP.palette;

  switch (planningEntityType) {
    case 'EMPLOYEE':
      return employee;
    case 'CLIENT':
      return client;
    case 'RESOURCE':
      return resource;
    case 'WORKPLACE':
      return workplace;
    case 'MIX':
      return theme.palette.primary.main;
  }

  return '';
};

export const getMatchingEntityIdentifiers = <
  A extends PlanningEntityIdentifier,
  B extends PlanningEntityIdentifier
>(
  entityIdentifiersA: A[] = [],
  entityIdentifiersB: B[] = []
) => {
  if (entityIdentifiersA.length === 0 || entityIdentifiersB.length === 0)
    return [];

  return entityIdentifiersA.filter((a) =>
    entityIdentifiersB.some(
      (b) =>
        // Note: we compare loosely (==) to allow null/undefined comparisons
        a.planningEntityType == b.planningEntityType && a.entityId == b.entityId
    )
  );
};

export const getSortedCalendars = (calendars: {
  [calendarId: string]: CalendarDisplayData;
}) => {
  const calendarList = Object.values(calendars);

  const sortedList = calendarList.sort(
    (
      { order: orderA = 0, sources: sourcesA = [] },
      { order: orderB = 0, sources: sourcesB = [] }
    ) => {
      const isMergedA = sourcesA.length > 1;
      const isMergedB = sourcesB.length > 1;

      if (isMergedA && !isMergedB) return -1;
      if (isMergedB && !isMergedA) return 1;

      return orderB - orderA;
    }
  );

  return sortedList;
};

export const formatEntityDisplayLabel = (
  label: string,
  number?: number | null
): string => {
  if (number != null) {
    return `${number} - ${label}`;
  }
  return label;
};

export const getUniqueEntities = (sources: CalendarSourceData[]) => {
  const entityIds = new Set<string>();

  sources.forEach(({ planningEntityIdentifier: { entityId } }) => {
    if (!entityIds.has(entityId!)) {
      entityIds.add(entityId!);
    }
  });

  return sources.filter(({ planningEntityIdentifier: { entityId } }) =>
    entityIds.has(entityId!)
  );
};
